import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "../index.css";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import GOQiiButton from "../Components/GoqiiButton";
import { navigateWithFai, triggerShare } from "../utils/Utils";
import { useTranslation } from "react-i18next";

// Shimmer helpers (same pattern as AgeCompare)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const HighchartSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        paddingBottom: 0,
        marginBottom: 0,
        position: "relative",
        paddingTop: 8,
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "55%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      </div>

      {/* Chart skeleton area */}
      <div
        style={{
          marginTop: 16,
          paddingInline: 16,
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 260,
            borderRadius: 16,
          }}
        />
      </div>

      {/* Bottom button skeleton */}
      <div
        style={{
          marginTop: 24,
          marginBottom: 24,
          display: "flex",
          justifyContent: "center",
          paddingInline: 16,
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 40,
            width: 180,
            borderRadius: 24,
          }}
        />
      </div>
    </div>
  );
};

const Highchart = ({ shareConfig } = {}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    const { encryptedId } = getUrlParams();

    if (!encryptedId) return;

    lifestyleApi
      .getHraData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setChartData(response.data.data);
        }
      })
      .catch((err) => console.error("Error loading HRA data:", err));
  }, []);

  if (!chartData) return <HighchartSkeleton />;


  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      spacingTop: 0,
      spacingBottom: 0,
      marginBottom: 100,
      spacingLeft: 20,
      spacingRight: 20,
      borderRadius: 20,
      height: 220,
      style: { fontFamily: "Open Sans, sans-serif" },

      events: {
        render() {
          const chart = this;
          const isMobile = chart.chartWidth < 768;
          const isSmallMobile = chart.chartWidth < 480;

          if (chart.customElements) chart.customElements.forEach((el) => el.destroy());
          chart.customElements = [];


          const headingFontSize = isSmallMobile ? "12px" : "14px";
          const headingMetrics = chart.renderer
            .text(chartData.heading)
            .css({ fontSize: headingFontSize, fontFamily: "Open Sans, sans-serif" })
            .getBBox();



          const subTextFontSize = "9px";
          const buttonWidth = isSmallMobile ? 120 : 140;
          const buttonHeight = isSmallMobile ? 35 : 40;
          const buttonX = chart.chartWidth / 2 - buttonWidth / 2;
          const buttonY = chart.chartHeight - buttonHeight - 20;

          const subTextY = chart.plotTop + chart.plotHeight + 60;


          const arrowParts = chartData.arrow ? chartData.arrow.split(" ") : [];
          const arrowType = arrowParts[0] || "fa-caret-right";
          const arrowColorFromData = arrowParts[1] || chartData.arrowColor;

          const arrowSymbol =
            arrowType === "fa-caret-up"
              ? "▲"
              : arrowType === "fa-caret-down"
                ? "▼"
                : "";

          const arrowColor =
            arrowColorFromData === "green"
              ? "#008000"
              : arrowColorFromData === "red"
                ? "#ff0000"
                : arrowColorFromData === "orange"
                  ? "#ff6600"
                  : arrowColorFromData;

          const numberTextContent = `${arrowSymbol} ${chartData.subText2}`;
          const sinceTextContent = chartData.subText3;

          const totalEstimatedWidth =
            (numberTextContent.length + sinceTextContent.length) *
            (isSmallMobile ? 6 : 7);
          const startX = chart.chartWidth / 2 - totalEstimatedWidth / 1.7;

          const numberText = chart.renderer
            .text(numberTextContent, startX, subTextY)
            .css({
              fontSize: subTextFontSize,
              fontWeight: "bold",
              color: arrowColor,
              fontFamily: "Open Sans, sans-serif",
            })
            .add();
          chart.customElements.push(numberText);

          const numberTextBBox = numberText.getBBox();

          const sinceText = chart.renderer
            .text(sinceTextContent, startX + numberTextBBox.width + 10, subTextY)
            .css({
              fontSize: subTextFontSize,
              fontWeight: "bold",
              color: "var(--text-secondary)",
              fontFamily: "Open Sans, sans-serif",
            })
            .add();
          chart.customElements.push(sinceText);


        },
      },

    },

    title: { text: null },

    xAxis: {
      categories: chartData.xAxis,
      lineColor: "var(--border-subtle)",
      lineWidth: 1,
      labels: {
        style: {
          fontSize: "11px",
          color: "var(--text-primary)",
          fontFamily: "Open Sans, sans-serif",
        },
      },
    },

    yAxis: {
      // min: 0,
      // max: 110,
      lineColor: "var(--border-subtle)",
      lineWidth: 0,
      gridLineColor: "var(--border-subtle)",
      gridLineWidth: 0,
      tickInterval: 20,
      title: { text: null },
      labels: {
        enabled: false
      },
    },

    legend: { enabled: false },
    tooltip: { enabled: false },

    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          inside: false,
          align: "center",
          verticalAlign: "top",
          y: -22,
          style: {
            fontSize: "12px",
            fontWeight: "bold",
            color: "var(--text-primary)",
            textOutline: "none",
            fontFamily: "Open Sans, sans-serif",
          },
          formatter: function () {
            return `${this.y}`;
          },
        },
        borderWidth: 0,
        borderRadius: 3,
        groupPadding: 0.1,
        pointPadding: 0.1,
        minPointLength: 3,
      },
      series: {
        colorByPoint: true,
      },
    },

    series: [
      {
        name: t("highchart.seriesName"),
        data: chartData.yAxis.map((p) => ({
          y: p.y,
          color: p.color,
        })),
      },
    ],

    credits: { enabled: false },
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        paddingBottom: 10,
        marginBottom: 0,
        position: "relative",
      }}
    >

      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/55898_1764052149.png"
        title={chartData.title}
        subtitle={chartData.heading}
        showInfo
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        onInfoClick={() => {
          navigateWithFai("HRA Info", chartData.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        containerProps={{
          style: {
            width: "100%",
            height: "100%",
          },
        }}
      />


      <div style={{
        position: "absolute",
        bottom: "8px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        display: "flex",
        justifyContent: "center"
      }}>
        <GOQiiButton
          text={chartData.buttonTitle}
          color="green"
          solid={true}
          shape="rounded_edges"
          font="bold"
          onClick={() => window.open(chartData.url, "_blank")}
        />
      </div>
    </div>
  );
};

export default Highchart;

/*end*/